// Table rows → the plain objects the engine reads. Pure: no database driver, no network,
// so the screens and the scripts share exactly one way of turning rows into a library.

import type {
  JobBomLineIn, JobIn, JobItemIn, JobRouteLineIn, LibCatalogItem, LibStandardChoice, Library, Scalar,
} from '@zerogap/engine';

export type Row = Record<string, any>;

/** The tables that make up a library snapshot. */
export const LIBRARY_TABLES = [
  'standards_version', 'labor_class', 'rate', 'work_center', 'phase', 'operation', 'time_standard',
  'vendor', 'material', 'catalog_item', 'catalog_input', 'catalog_derived', 'recipe', 'recipe_route_step',
  'recipe_bom_line', 'catalog_child', 'catalog_part', 'standard_def', 'setting',
] as const;

export type LibraryRows = Record<(typeof LIBRARY_TABLES)[number], Row[]>;

const text = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
const time = (v: unknown): number => (v ? new Date(v as string).getTime() : 0);

/** The standards version in force: the latest by effective date. */
export function currentVersion(versions: Row[]): Row | undefined {
  return [...versions].sort((a, b) => time(b.effective_at) - time(a.effective_at) || time(b.created_at) - time(a.created_at))[0];
}

export function buildLibrary(rows: LibraryRows, versionId?: string | null): Library {
  const version = (versionId ? rows.standards_version.find((v) => v.id === versionId) : undefined) ?? currentVersion(rows.standards_version);
  if (!version) throw new Error('The library has no standards version yet. Run the seed.');

  const minutes = new Map<string, string | null>();
  for (const t of rows.time_standard) {
    if (t.standards_version_id === version.id) minutes.set(t.operation_id, text(t.minutes_per_unit));
  }
  const rates: Record<string, string> = {};
  for (const r of rows.rate) {
    if (r.standards_version_id === version.id) rates[r.labor_class_id] = String(r.dollars_per_hour);
  }

  const lib: Library = {
    standardsVersion: { id: version.id, name: version.name },
    laborClasses: {},
    rates,
    operations: {},
    materials: {},
    catalog: {},
    standards: [],
    pricing: { labor_contingency_pct: 0, materials_markup_pct: 0, sub_markup_pct: 0, gross_margin_pct: 0 },
    phases: {},
    workCenters: {},
  };

  for (const c of rows.labor_class) lib.laborClasses[c.id] = { id: c.id, name: c.name, sort: c.sort ?? 0 };
  for (const p of rows.phase) lib.phases[p.id] = { id: p.id, name: p.name, sort: p.sort ?? 0 };
  for (const w of rows.work_center) lib.workCenters[w.id] = { id: w.id, name: w.name };

  for (const o of rows.operation) {
    lib.operations[o.id] = {
      id: o.id,
      admin004_id: text(o.admin004_id),
      name: o.name,
      category: text(o.category),
      labor_class_id: o.labor_class_id ?? null,
      phase_id: o.phase_id ?? null,
      work_center_id: o.work_center_id ?? null,
      unit: text(o.unit),
      minutes_per_unit: minutes.get(o.id) ?? null,
      setup_minutes: text(o.setup_minutes),
      batch_size: text(o.batch_size),
      verified: !!o.verified,
      retired: !!o.retired,
    };
  }
  const vendorName = new Map(rows.vendor.map((v) => [v.id as string, v.name as string]));
  for (const m of rows.material) {
    lib.materials[m.id] = {
      id: m.id,
      admin005_id: text(m.admin005_id),
      description: m.description,
      price: text(m.price),
      unit: text(m.unit),
      vendor: m.vendor_id ? vendorName.get(m.vendor_id) ?? null : null,
      category: text(m.category),
      subcategory: text(m.subcategory),
      verified: !!m.verified,
      nominal_thickness_in: text(m.nominal_thickness_in),
      undersize_in: text(m.undersize_in),
      sheet_length_in: text(m.sheet_length_in),
      sheet_width_in: text(m.sheet_width_in),
      offered_as: Array.isArray(m.offered_as) ? m.offered_as.map(String) : [],
      pack_size: text(m.pack_size),
      whole_packs: !!m.whole_packs,
      // retired (2026-10-23): kept for the lines that name it, offered to no new one
      retired: !!m.retired_at,
    };
  }

  const bySort = (a: Row, b: Row) => (a.sort ?? 0) - (b.sort ?? 0);
  for (const c of rows.catalog_item) {
    const recipeRows = rows.recipe.filter((r) => r.catalog_item_id === c.id);
    const item: LibCatalogItem = {
      id: c.id,
      name: c.name,
      family: text(c.family),
      mark_threshold: c.mark_threshold === 'sub_item' ? 'sub_item' : c.mark_threshold === 'no_mark' ? 'no_mark' : 'top_tier',
      version: c.version ?? 1,
      // an item is verified only when every recipe under it is; the row's own flag is a mirror the database keeps
      verified: recipeRows.length > 0 && recipeRows.every((r) => !!r.verified),
      retired: !!c.retired,
      grid_enabled: !!c.grid_enabled,
      compare_as: text(c.compare_as_expr),
      recipe_standard: text(c.recipe_standard),
      sub_lines_off_expr: text(c.sub_lines_off_expr),
      dimension_roles: c.dimension_roles && typeof c.dimension_roles === 'object' ? (c.dimension_roles as Record<string, string>) : {},
      // a row an AI retired (2026-10-23) stays in the table behind Show retired and is not part of the item the engine prices
      inputs: rows.catalog_input
        .filter((i) => i.catalog_item_id === c.id && !i.retired_at)
        .sort(bySort)
        .map((i) => ({
          key: i.key,
          label: i.label,
          unit: text(i.unit),
          data_type: i.data_type,
          default_value: text(i.default_value),
          choices: Array.isArray(i.choices) ? i.choices.map(String) : null,
          sort: i.sort ?? 0,
          rule_expr: text(i.rule_expr),
          rule_message: text(i.rule_message),
          info: text(i.info),
          material_filter: i.material_filter && typeof i.material_filter === 'object' ? i.material_filter : null,
          standard_key: text(i.standard_key),
          hidden_expr: text(i.hidden_expr),
        })),
      derived: rows.catalog_derived
        .filter((d) => d.catalog_item_id === c.id && !d.retired_at)
        .sort(bySort)
        .map((d) => ({ key: d.key, label: d.label, expr: d.expr, sort: d.sort ?? 0 })),
      recipes: recipeRows
        .map((r) => ({
          id: r.id,
          name: r.name,
          version: r.version ?? 1,
          verified: !!r.verified,
          is_default: !!r.is_default,
          unavailable_expr: text(r.unavailable_expr),
          route: rows.recipe_route_step
            .filter((s) => s.recipe_id === r.id && !s.retired_at)
            .sort(bySort)
            .map((s) => ({
              id: s.id, sort: s.sort ?? 0, operation_id: s.operation_id, qty_expr: s.qty_expr,
              charge_setup: s.charge_setup !== false, note: text(s.note),
            })),
          bom: rows.recipe_bom_line
            .filter((b) => b.recipe_id === r.id && !b.retired_at)
            .sort(bySort)
            .map((b) => ({
              id: b.id, sort: b.sort ?? 0, material_id: b.material_id ?? null, material_role: text(b.material_role),
              qty_expr: b.qty_expr, note: text(b.note),
              label: text(b.label), unit_price_expr: text(b.unit_price_expr), sub_expr: text(b.sub_expr), link_expr: text(b.link_expr),
              separate_pricing: !!b.separate_pricing, markup_pct: text(b.markup_pct), ignore_margin: !!b.ignore_margin,
            })),
          // the parts this recipe cuts the item from, in the order the cut list shows them; a bought recipe has none
          parts: (rows.catalog_part ?? [])
            .filter((p) => p.recipe_id === r.id && !p.retired_at)
            .sort((a, b) => bySort(a, b) || time(a.created_at) - time(b.created_at) || String(a.id).localeCompare(String(b.id)))
            .map((p) => ({
              id: p.id,
              sort: p.sort ?? 0,
              name: p.name,
              qty_expr: p.qty_expr ?? '1',
              length_expr: p.length_expr ?? '0',
              width_expr: p.width_expr ?? '0',
              label: text(p.label),
              material_id: p.material_id ?? null,
              material_role: text(p.material_role),
              grain: p.grain === 'width' || p.grain === 'none' ? p.grain : 'length',
              band_long_expr: text(p.band_long_expr),
              band_short_expr: text(p.band_short_expr),
              grain_locked: !!p.grain_locked,
              band_material_expr: text(p.band_material_expr),
              note: text(p.note),
            })),
        })),
      // links run in their own order: it is the order the children are numbered in
      children: rows.catalog_child
        .filter((k) => k.catalog_item_id === c.id && !k.retired_at)
        .sort((a, b) => bySort(a, b) || time(a.created_at) - time(b.created_at) || String(a.id).localeCompare(String(b.id)))
        .map((k) => ({
          id: k.id,
          label: text(k.label),
          child_catalog_item_id: k.child_catalog_item_id ?? null,
          child_expr: text(k.child_expr),
          count_expr: k.count_expr,
          input_map: (k.input_map ?? {}) as Record<string, string>,
        })),
    };
    lib.catalog[c.id] = item;
  }

  lib.standards = [...rows.standard_def].sort(bySort).map((d) => ({
    key: d.key,
    label: d.label,
    kind: d.kind,
    data_type: d.data_type,
    default_value: text(d.default_value),
    choices: Array.isArray(d.choices) ? (d.choices as LibStandardChoice[]) : null,
    sort: d.sort ?? 0,
    unit: text(d.unit),
    material_filter: d.material_filter && typeof d.material_filter === 'object' ? d.material_filter : null,
    roles: d.roles && typeof d.roles === 'object' ? d.roles : null,
    allow_item_override: d.allow_item_override !== false,
  }));

  const pricing = rows.setting.find((s) => s.key === 'pricing')?.value;
  if (pricing && typeof pricing === 'object') {
    lib.pricing = {
      labor_contingency_pct: pricing.labor_contingency_pct ?? 0,
      materials_markup_pct: pricing.materials_markup_pct ?? 0,
      sub_markup_pct: pricing.sub_markup_pct ?? 0,
      gross_margin_pct: pricing.gross_margin_pct ?? 0,
      // the company's sales tax rate is its own setting (Admin003's Company_Standards had it), read beside the percentages
      sales_tax_pct: rows.setting.find((s) => s.key === 'tax_rate_pct')?.value ?? 0,
    };
    // the estimate range (Spencer, 2026-10-22): its own setting, read beside the percentages
    const range = rows.setting.find((s) => s.key === 'estimate_range')?.value;
    if (range && typeof range === 'object') {
      lib.pricing.estimate_range_down_pct = range.down_pct ?? 0;
      lib.pricing.estimate_range_up_pct = range.up_pct ?? 0;
    }
  }
  // how far sheets and setups pool
  const pooling = rows.setting.find((s) => s.key === 'pooling')?.value;
  lib.pooling = pooling && typeof pooling === 'object' && pooling.scope ? { scope: String(pooling.scope) } : { scope: 'job' };
  return lib;
}

export interface JobRows {
  job: Row;
  job_standard: Row[];
  job_item: Row[];
  sub_line: Row[];
  decision_line: Row[];
  /** Job-wide costs (tax, shipping, handling). Optional for callers written before it existed. */
  job_cost_line?: Row[];
  /** The job's rooms and the standards a room holds for itself. Optional for callers written before rooms existed. */
  room?: Row[];
  room_standard?: Row[];
  /** The job route (Spencer, 2026-10-08): the job type's steps and lines, and what this job typed against them or added. */
  job_type_route_step?: Row[];
  job_type_bom_line?: Row[];
  job_route_line?: Row[];
  job_bom_line?: Row[];
}

/**
 * The job's route: every step of its type's route, with the quantity the job typed against it (or, with nothing
 * typed, the quantity the type starts a job with), followed by the steps the job added on its own. The same for
 * its BOM lines.
 */
export function jobRouteLines(rows: JobRows): { routeLines: JobRouteLineIn[]; bomLines: JobBomLineIn[] } {
  const typeId = rows.job.job_type_id ?? null;
  const bySort = (a: Row, b: Row) => (a.sort ?? 0) - (b.sort ?? 0) || time(a.created_at) - time(b.created_at);
  const typeSteps = (rows.job_type_route_step ?? []).filter((s) => typeId && s.job_type_id === typeId).sort(bySort);
  const jobLines = (rows.job_route_line ?? []).filter((l) => l.job_id === rows.job.id).sort(bySort);
  const routeLines: JobRouteLineIn[] = [
    ...typeSteps.map((s) => {
      const own = jobLines.find((l) => l.type_step_id === s.id);
      // the job starts at the quantity its type gives the step; what the job typed (a number, or a blank) wins
      return { id: own?.id ?? `type:${s.id}`, operation_id: s.operation_id, qty: text(own ? own.qty : s.qty), note: text(own?.note ?? s.note), type_step_id: s.id as string };
    }),
    ...jobLines.filter((l) => !l.type_step_id || !typeSteps.some((s) => s.id === l.type_step_id)).map((l) => ({ id: l.id, operation_id: l.operation_id, qty: text(l.qty), note: text(l.note), type_step_id: null })),
  ];
  const typeLines = (rows.job_type_bom_line ?? []).filter((s) => typeId && s.job_type_id === typeId).sort(bySort);
  const jobBom = (rows.job_bom_line ?? []).filter((l) => l.job_id === rows.job.id).sort(bySort);
  const bomLines: JobBomLineIn[] = [
    ...typeLines.map((s) => {
      const own = jobBom.find((l) => l.type_line_id === s.id);
      return { id: own?.id ?? `type:${s.id}`, material_id: s.material_id, qty: text(own ? own.qty : s.qty), note: text(own?.note ?? s.note), type_line_id: s.id as string };
    }),
    ...jobBom.filter((l) => !l.type_line_id || !typeLines.some((s) => s.id === l.type_line_id)).map((l) => ({ id: l.id, material_id: l.material_id, qty: text(l.qty), note: text(l.note), type_line_id: null })),
  ];
  return { routeLines, bomLines };
}

export function buildJob(rows: JobRows, standardDefs: Row[]): JobIn {
  const keyOf = new Map(standardDefs.map((d) => [d.id, d.key as string]));
  const standards: Record<string, Scalar> = {};
  for (const s of rows.job_standard) {
    const key = keyOf.get(s.standard_def_id);
    if (key) standards[key] = text(s.value);
  }
  const rooms = (rows.room ?? [])
    .slice()
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || time(a.created_at) - time(b.created_at))
    .map((r) => {
      const own: Record<string, Scalar> = {};
      for (const s of rows.room_standard ?? []) {
        if (s.room_id !== r.id) continue;
        const key = keyOf.get(s.standard_def_id);
        if (key) own[key] = text(s.value);
      }
      return { id: r.id, name: r.name ?? '', sort: r.sort ?? 0, own_standards: !!r.own_standards, standards: own };
    });
  // a retired item (2026-10-23) is out of every price, and so are the rows under it, until Restore
  const retired = new Set(rows.job_item.filter((i) => i.retired_at).map((i) => i.id as string));
  const live = rows.job_item.filter((i) => !i.retired_at && !(i.parent_item_id && retired.has(i.parent_item_id as string)));
  const items: JobItemIn[] = live.map((i) => ({
    id: i.id,
    number: i.number ?? '',
    name: i.name ?? '',
    catalog_item_id: i.catalog_item_id ?? null,
    recipe_id: i.recipe_id ?? null,
    parent_item_id: i.parent_item_id ?? null,
    room_id: i.room_id ?? null,
    child_index: i.child_index ?? null,
    child_link_id: i.catalog_child_id ?? null,
    inputs: i.inputs ?? {},
    overrides: i.overrides ?? {},
    is_custom: !!i.is_custom,
    custom_route: Array.isArray(i.custom_route) ? i.custom_route : [],
    custom_bom: Array.isArray(i.custom_bom) ? i.custom_bom : [],
    note: text(i.note),
    sort: i.sort ?? 0,
    add_on_id: i.add_on_id ?? null,
    replaces_item_id: i.replaces_item_id ?? null,
    children_left_out: Array.isArray(i.children_left_out) ? i.children_left_out.map(String) : [],
  }));
  return {
    id: rows.job.id,
    standards,
    items,
    subLines: rows.sub_line.map((s) => ({
      id: s.id,
      scope_text: s.scope_text,
      internal_estimate: text(s.internal_estimate),
      quote_amount: text(s.quote_amount),
      use_quote: !!s.use_quote,
      job_item_id: s.job_item_id ?? null,
      markup_pct: text(s.markup_pct),
      // the line's cell per build (2026-10-24): a cost typed for a build, or left out of its price by hand
      build_overrides: s.build_overrides && typeof s.build_overrides === 'object' && !Array.isArray(s.build_overrides) ? (s.build_overrides as Record<string, { amount?: string | number | null; left_out?: boolean }>) : null,
    })),
    decisionLines: rows.decision_line.map((d) => ({ id: d.id, kind: d.kind, label: d.label, amount: text(d.amount) })),
    costLines: (rows.job_cost_line ?? []).map((c) => ({ id: c.id, label: c.label, amount: text(c.amount), separate_pricing: !!c.separate_pricing, markup_pct: text(c.markup_pct), ignore_margin: !!c.ignore_margin })),
    pricing: rows.job.pricing_overrides && typeof rows.job.pricing_overrides === 'object' ? rows.job.pricing_overrides : undefined,
    rooms,
    ...jobRouteLines(rows),
  };
}
